import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  Typography, Button, Box, Container, IconButton, Drawer, List, ListItem,
  ListItemButton, ListItemText, useMediaQuery, useTheme,
} from '@mui/material'
import {
  Brightness4, Brightness7, Menu as MenuIcon, Close as CloseIcon,
} from '@mui/icons-material'
import { useColorMode } from '../../theme'
import { FolioMark } from '../FolioMark'

/** Public pages the nav links to, in order. Single source for desktop + drawer. */
export const SITE_NAV_ITEMS = [
  { label: 'Docs', path: '/docs' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Demo', path: '/demo' },
  { label: 'Sign in', path: '/login' },
] as const

interface SiteNavProps {
  /** Extra element rendered under the bar — the docs layout puts its
      breadcrumb / mobile sidebar trigger here so it shares the sticky block. */
  slot?: React.ReactNode
}

/**
 * The marketing-site nav bar.
 *
 * Lifted verbatim out of the landing page when /docs was added. Reason: the
 * docs section is meant to be indistinguishable from the marketing site, and
 * two hand-maintained copies of a nav drift within a release or two.
 */
export function SiteNav({ slot }: SiteNavProps) {
  const theme = useTheme()
  const { toggleColorMode, mode } = useColorMode()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileOpen, setMobileOpen] = useState(false)

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolioMark size={24} /> FolioWorth
        </Typography>
        <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {SITE_NAV_ITEMS.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton component={Link} to={item.path} onClick={() => setMobileOpen(false)}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Button component={Link} to="/register" variant="contained" sx={{ mt: 2 }}>
        Start free
      </Button>
    </Box>
  )

  return (
    <>
      <Box component="nav" sx={{
        position: 'sticky', top: 0, zIndex: 10,
        backdropFilter: 'blur(12px)',
        background: 'color-mix(in srgb, var(--bg) 82%, transparent)',
        borderBottom: '1px solid var(--border-soft)',
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Typography component={Link} to="/" sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.05rem',
              color: 'inherit', textDecoration: 'none',
            }}>
              <FolioMark size={26} title="FolioWorth" /> FolioWorth
            </Typography>

            {isMobile ? (
              <Box>
                <IconButton onClick={toggleColorMode} aria-label="Toggle theme">
                  {mode === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
                </IconButton>
                <IconButton onClick={() => setMobileOpen(true)} aria-label="Open menu">
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {SITE_NAV_ITEMS.map((item) => (
                  <Button key={item.label} component={Link} to={item.path} color="inherit" size="small">
                    {item.label}
                  </Button>
                ))}
                <IconButton onClick={toggleColorMode} size="small" aria-label="Toggle theme">
                  {mode === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
                </IconButton>
                <Button component={Link} to="/register" variant="contained" size="small" sx={{ ml: 1 }}>
                  Start free
                </Button>
              </Box>
            )}
          </Box>
          {slot}
        </Container>
      </Box>

      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { width: 280 } }}>
        {drawer}
      </Drawer>
    </>
  )
}
